import { ImMNElement } from '../../../common/elements/elementModelTypes';
import { getElementId, getXPosition, getYPosition } from '../../../common/elements/utils/elementPropertyUtils';
import { getElementDOMMeasurement } from '../../utils/domUtil';
import { MaintainAspectRatioMode, ResizeAspectLock, ResizeChanges } from './resizeTypes';
import { RectSize } from '../../../common/maths/geometry/rect/rectTypes';
import { sortBy2dPositionTopLeftToBottomRight } from '../../../common/elements/utils/elementSortUtils';
import { prop, toArray } from '../../../common/utils/immutableHelper';
import { getElementSyncedMeasurements } from '../../components/measurementsStore/elementMeasurements/syncedMeasurementsSingleton';
import { BORDER_OFFSET_PX } from '../../utils/grid/gridUtils';
import { ELEMENT_DEFAULT_WIDTH } from '../../../common/elements/elementConstants';

const TOTAL_GROUP_ANIMATION_OFFSET = 500;
const MAX_ELEMENT_ANIMATION_OFFSET = 50;
const MAX_ANIMATION_OFFSET_ELEMENT_COUNT = 50;
const SNAP_THRESHOLD = 2;

export const beginAnimationOrchestration = (resizingElements: ImMNElement[], callbacksMap: Map<string, () => void>) => {
    const resizingElementsArray = toArray<ImMNElement>(resizingElements);
    if (!resizingElementsArray) return;

    const orderedCallbacks = resizingElementsArray
        .sort(sortBy2dPositionTopLeftToBottomRight)
        .map((el) => callbacksMap.get(getElementId(el)))
        .filter(Boolean) as (() => void)[];

    const resizingCount = orderedCallbacks.length;
    if (resizingCount === 0) return;

    // Over a maximum number of elements, just run all the callbacks immediately
    if (resizingCount > MAX_ANIMATION_OFFSET_ELEMENT_COUNT) {
        orderedCallbacks.forEach((cb) => cb());
        return;
    }

    const animationDelay = Math.min(TOTAL_GROUP_ANIMATION_OFFSET / resizingCount, MAX_ELEMENT_ANIMATION_OFFSET);
    orderedCallbacks.forEach((cb, index) => {
        setTimeout(cb, index * animationDelay);
    });
};

export const getTopAndLeftElements = (
    elements: Array<ImMNElement>,
): { leftElement: ImMNElement; topElement: ImMNElement } => {
    const leftElement = elements.reduce((leftMost, current) => {
        if (!leftMost) return current;
        return getXPosition(current) < getXPosition(leftMost) ? current : leftMost;
    }, elements[0]);

    const topElement = elements.reduce((topMost, current) => {
        if (!topMost) return current;
        return getYPosition(current) < getYPosition(topMost) ? current : topMost;
    }, elements[0]);

    return {
        leftElement,
        topElement,
    };
};

export const getNewResizeScale = (start: number, current: number, origin: number) =>
    (current - origin) / (start - origin);

export const getGroupClientOrigin = (leftElementId: string, topElementId: string) => {
    const leftElementBoundingRect = getElementDOMMeasurement(leftElementId);
    const topElementBoundingRect =
        leftElementId === topElementId ? leftElementBoundingRect : getElementDOMMeasurement(topElementId);

    return {
        x: leftElementBoundingRect?.x || 0,
        y: topElementBoundingRect?.y || 0,
    };
};

export const getCurrentElementSize = (elementId: string, gridSize: number): RectSize | null => {
    // Use synced measurements singleton to ensure we get the latest measurements.
    // This is especially important for elements like cards that may have had measurements paused during editing.
    const savedMeasurements = getElementSyncedMeasurements(elementId);

    if (!savedMeasurements) return null;

    // We need to add BORDER_OFFSET_PX to the width since it will be subtracted in the element style
    const width = (prop('width', savedMeasurements) + BORDER_OFFSET_PX) / gridSize;
    const height = prop('height', savedMeasurements) / gridSize;

    return { width, height };
};

export const getCanChangeAspectRatio = (
    maintainAspectMode: MaintainAspectRatioMode,
    allowAspectChangeForCurrentResize: boolean,
): boolean => {
    if (maintainAspectMode === MaintainAspectRatioMode.ALWAYS) return false;
    if (maintainAspectMode === MaintainAspectRatioMode.NEVER) return true;

    return allowAspectChangeForCurrentResize;
};

export const getResizeAspectLock = (canChangeAspectRatio: boolean, scalingElementsCount: number): ResizeAspectLock => {
    if (canChangeAspectRatio) return ResizeAspectLock.NONE;
    if (scalingElementsCount > 1) return ResizeAspectLock.ELEMENT;
    return ResizeAspectLock.MEDIA;
};

export const isChangeTypeofRectSize = (obj: ResizeChanges): obj is RectSize =>
    !!obj && typeof obj.width === 'number' && typeof obj.height === 'number';

/**
 * Snap size to default width if applicable, otherwise return original size.
 */
export const getConditionallySnappedSize = (size: RectSize, isMultiResizingOrAnimating: boolean) => {
    if (isMultiResizingOrAnimating) return size;

    const { width } = size;

    const defaultWidthDiff = Math.abs(width - ELEMENT_DEFAULT_WIDTH);

    return {
        ...size,
        width: defaultWidthDiff < SNAP_THRESHOLD ? ELEMENT_DEFAULT_WIDTH : width,
    };
};
